import {
  Locale,
  type LocalizedStrings,
  useLocalization,
} from "~/components/localization/LocalizationProvider";
import { HeroStyle2 } from "~/components/HeroStyle2";
import { Box, Flex, Grid, Heading, Text } from "@radix-ui/themes";
import styles from "./orthodontics-for-adults.module.css";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import { useState, useRef, useEffect } from "react";
import { Link } from "~/components/RouterLink";

type Localization = {
  title: string;
  subtitle: string;
  introFirstParagraph: string;
  introSecondParagraph: string;
  introThirdParagraph: string;
  moreAdultsTitle: string;
  moreAdultsIntro: string;
  moreAdultsWhy: string;
  lookingGoodTitle: string;
  lookingGoodContent: string;
  teethCanShiftTitle: string;
  teethCanShiftContent: string;
  dentalHealthTitle: string;
  dentalHealthContent: string;
  faqTitle: string;
  faqItems: Array<{
    question: string;
    answer: string[];
  }>;
};

const englishLocalization: Localization = {
  title: "Orthodontics for Adults",
  subtitle: "Adult Orthodontic Services",
  introFirstParagraph:
    "Regardless of your age, orthodontic treatment can help you obtain a beautiful, straight smile and a healthy occlusion (bite). Whether you've had braces in the past and want to correct a relapse or you're looking to start treatment for the first time, Dr. Garcia-Paul will help you transform your smile.",
  introSecondParagraph:
    "Adult orthodontic treatment is becoming more and more common. In our office, over a third of our patients are adults. Several cosmetic options are available to serve this growing population.",
  introThirdParagraph:
    'For patients concerned about the appearance of metal braces, Dr. Garcia-Paul offers clear braces, lingual "hidden" braces and Invisalign. Call our office today to schedule a complimentary consultation where we can find the option that best fits your needs.',
  moreAdultsTitle: "More Adults Are Pursuing Orthodontics",
  moreAdultsIntro:
    "You’re never too old to benefit from orthodontic treatment. Today, adults make up about one in every five orthodontic patients.",
  moreAdultsWhy: "Here’s why:",
  lookingGoodTitle: "Looking Good",
  lookingGoodContent:
    "Orthodontic treatment can help correct crooked teeth or spaces that you've been self-conscious about for years. It can give you the confidence and pride that come with straight teeth and a great smile.",
  teethCanShiftTitle: "Teeth Can Shift",
  teethCanShiftContent:
    "Believe it or not, your teeth don't stop moving in adolescence. Teeth that seemed straight and aligned in youth could have shifted later in life. This can lead to jaw pain, trouble brushing, or a smile you're unhappy with.",
  dentalHealthTitle: "Maintaining Dental Health",
  dentalHealthContent:
    "If you leave misaligned teeth untreated, you may end up with other dental problems such as tooth decay and gum disease. You may also notice difficulty chewing or abnormal wear of the surface of your teeth.",
  faqTitle: "Adult Orthodontics FAQ",
  faqItems: [
    {
      question: "Am I a good fit for orthodontic treatment?",
      answer: [
        "During your consultation, Dr. Garcia-Paul can determine if you will benefit from treatment.",
        "After carefully analyzing the nature of your needed correction, Dr. Garcia-Paul will recommend the best course of treatment for improving your smile, including the different types of braces that are best for you.",
      ],
    },
    {
      question: "Does orthodontic treatment work for adults?",
      answer: [
        "Yes. Healthy teeth can be moved at any age.",
        "Many orthodontic problems can be corrected as easily for adults as for children. Orthodontic forces move teeth in the same way for both adults and children.",
      ],
    },
    {
      question: "Do my appliances have to show?",
      answer: [
        "Many options are available today to minimize the appearance of braces. Today's braces are smaller and sleeker, and some are next-to-invisible.",
        "Dr. Garcia-Paul can advise you about options that will fit your particular needs. You can find more information about types of braces, including Invisalign, here.",
        "These options allow you to have flexibility and discretion during your treatment.",
      ],
    },
    {
      question: "Will orthodontic treatment fit my lifestyle?",
      answer: [
        "You can do almost anything during orthodontic treatment – sing, play a musical instrument, dine out, kiss, and even have your picture taken. Some adjustments may be necessary. But, if you're like the vast majority of adult patients, you'll adjust quickly.",
        "New techniques and materials have made wearing braces more comfortable, and convenient than ever.",
      ],
    },
    {
      question:
        "I'm missing some teeth. Can orthodontic treatment improve my bite?",
      answer: [
        "Orthodontic treatment can create or hold space to replace missing teeth.",
        "Dr. Garcia-Paul will work with your dentist or dental specialist. They will provide bridges or implants so that you can achieve the best result.",
      ],
    },
    {
      question: "How much does treatment cost?",
      answer: [
        "The cost of orthodontic treatment will depend on many factors, including the severity of the problem, its complexity and length of treatment. Dr. Garcia-Paul will be glad to discuss the cost with you before treatment begins.",
        "You will find that braces are more affordable today than ever. We offer a variety of convenient payment plans, and accept many dental insurance plans that include orthodontic benefits.",
      ],
    },
    {
      question:
        "Why choose an orthodontist over aligners that come in the mail?",
      answer: [
        "Orthodontists are dental specialists who diagnose, prevent and treat dental and facial irregularities.",
        "Dr. Garcia-Paul received an additional three full years of specialized education beyond dental school to learn the proper way to align and straighten teeth.",
        'Only those with this formal education may call themselves "orthodontists," and only orthodontists may be members of the American Association of Orthodontists (AAO).',
      ],
    },
    {
      question:
        "I've been treated for gum disease. Can my teeth be straightened?",
      answer: [
        "Dr. Garcia-Paul will consult with your dentist and/or periodontist about the health of your gums.",
        "If you are a candidate for orthodontic treatment, you will continue regular supervision with your dentist and periodontist. In many cases, tooth alignment can improve the health of the gums.",
      ],
    },
  ],
};

const spanishLocalization: Localization = {
  title: "Ortodoncia para Adultos",
  subtitle: "Servicios de Ortodoncia para Adultos",
  introFirstParagraph:
    "Independientemente de su edad, el tratamiento de ortodoncia puede ayudarle a obtener una sonrisa hermosa y recta y una oclusión (mordida) saludable. Ya sea que haya tenido brackets en el pasado y desee corregir una recaída o esté buscando comenzar el tratamiento por primera vez, la Dra. García-Paul le ayudará a transformar su sonrisa.",
  introSecondParagraph:
    "El tratamiento de ortodoncia para adultos es cada vez más común. En nuestra oficina, más de un tercio de nuestros pacientes son adultos. Hay varias opciones cosméticas disponibles para atender a esta creciente población.",
  introThirdParagraph:
    'Para los pacientes preocupados por la apariencia de los brackets metálicos, la Dra. García-Paul ofrece brackets transparentes, brackets linguales "ocultos" e Invisalign. Llame a nuestra oficina hoy para programar una consulta gratuita donde podemos encontrar la opción que mejor se adapte a sus necesidades.',
  moreAdultsTitle: "Más Adultos Están Buscando Ortodoncia",
  moreAdultsIntro:
    "Nunca es demasiado tarde para beneficiarse del tratamiento de ortodoncia. Hoy en día, los adultos representan aproximadamente uno de cada cinco pacientes de ortodoncia.",
  moreAdultsWhy: "Estas son las razones:",
  lookingGoodTitle: "Verse Bien",
  lookingGoodContent:
    "El tratamiento de ortodoncia puede ayudar a corregir dientes torcidos o espacios que le han causado inseguridad durante años. Puede darle la confianza y el orgullo que vienen con dientes rectos y una gran sonrisa.",
  teethCanShiftTitle: "Los Dientes Pueden Moverse",
  teethCanShiftContent:
    "Aunque no lo crea, sus dientes no dejan de moverse en la adolescencia. Los dientes que parecían rectos y alineados en la juventud podrían haberse desplazado más adelante en la vida. Esto puede provocar dolor en la mandíbula, dificultad para cepillarse o una sonrisa con la que no está contento.",
  dentalHealthTitle: "Manteniendo la Salud Dental",
  dentalHealthContent:
    "Si deja los dientes desalineados sin tratar, puede terminar con otros problemas dentales como caries y enfermedad de las encías. También puede notar dificultad para masticar o desgaste anormal de la superficie de sus dientes.",
  faqTitle: "Preguntas Frecuentes sobre Ortodoncia para Adultos",
  faqItems: [
    {
      question: "¿Soy un buen candidato para el tratamiento de ortodoncia?",
      answer: [
        "Durante su consulta, la Dra. García-Paul puede determinar si se beneficiará del tratamiento.",
        "Después de analizar cuidadosamente la naturaleza de la corrección necesaria, la Dra. García-Paul recomendará el mejor curso de tratamiento para mejorar su sonrisa, incluidos los diferentes tipos de brackets que son mejores para usted.",
      ],
    },
    {
      question: "¿Funciona el tratamiento de ortodoncia para adultos?",
      answer: [
        "Sí. Los dientes sanos se pueden mover a cualquier edad.",
        "Muchos problemas de ortodoncia pueden corregirse tan fácilmente para adultos como para niños. Las fuerzas ortodónticas mueven los dientes de la misma manera tanto para adultos como para niños.",
      ],
    },
    {
      question: "¿Tienen que verse mis aparatos?",
      answer: [
        "Hoy en día hay muchas opciones disponibles para minimizar la apariencia de los brackets. Los brackets actuales son más pequeños y elegantes, y algunos son casi invisibles.",
        "La Dra. García-Paul puede aconsejarle sobre las opciones que se adaptarán a sus necesidades particulares. Puede encontrar más información sobre los tipos de brackets, incluido Invisalign, aquí.",
        "Estas opciones le permiten tener flexibilidad y discreción durante su tratamiento.",
      ],
    },
    {
      question:
        "¿El tratamiento de ortodoncia se adaptará a mi estilo de vida?",
      answer: [
        "Puede hacer casi cualquier cosa durante el tratamiento de ortodoncia: cantar, tocar un instrumento musical, cenar fuera, besar e incluso tomarse fotos. Algunos ajustes pueden ser necesarios. Pero, si es como la gran mayoría de los pacientes adultos, se adaptará rápidamente.",
        "Las nuevas técnicas y materiales han hecho que usar brackets sea más cómodo y conveniente que nunca.",
      ],
    },
    {
      question:
        "Me faltan algunos dientes. ¿Puede el tratamiento de ortodoncia mejorar mi mordida?",
      answer: [
        "El tratamiento de ortodoncia puede crear o mantener espacio para reemplazar los dientes faltantes.",
        "La Dra. García-Paul trabajará con su dentista o especialista dental. Ellos proporcionarán puentes o implantes para que pueda lograr el mejor resultado.",
      ],
    },
    {
      question: "¿Cuánto cuesta el tratamiento?",
      answer: [
        "El costo del tratamiento de ortodoncia dependerá de muchos factores, incluida la gravedad del problema, su complejidad y la duración del tratamiento. La Dra. García-Paul estará encantada de discutir el costo con usted antes de comenzar el tratamiento.",
        "Descubrirá que los brackets son más asequibles hoy que nunca. Ofrecemos una variedad de planes de pago convenientes y aceptamos muchos planes de seguro dental que incluyen beneficios de ortodoncia.",
      ],
    },
    {
      question:
        "¿Por qué elegir un ortodoncista en lugar de alineadores que vienen por correo?",
      answer: [
        "Los ortodoncistas son especialistas dentales que diagnostican, previenen y tratan irregularidades dentales y faciales.",
        "La Dra. García-Paul recibió tres años completos adicionales de educación especializada más allá de la escuela dental para aprender la forma correcta de alinear y enderezar los dientes.",
        'Solo aquellos con esta educación formal pueden llamarse "ortodoncistas", y solo los ortodoncistas pueden ser miembros de la Asociación Americana de Ortodoncistas (AAO).',
      ],
    },
    {
      question:
        "He sido tratado por enfermedad de las encías. ¿Se pueden enderezar mis dientes?",
      answer: [
        "La Dra. García-Paul consultará con su dentista y/o periodoncista sobre la salud de sus encías.",
        "Si es candidato para el tratamiento de ortodoncia, continuará con la supervisión regular de su dentista y periodoncista. En muchos casos, la alineación de los dientes puede mejorar la salud de las encías.",
      ],
    },
  ],
};

const localized: LocalizedStrings<Localization> = {
  [Locale.EN]: englishLocalization,
  [Locale.ES]: spanishLocalization,
};

export function OrthodonticsForAdultsPage() {
  const getLocalization = useLocalization(localized);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const answerRefs = useRef<(HTMLDivElement | null)[]>([]);

  const toggleFaq = (index: number) => {
    if (expandedFaq === index) {
      setExpandedFaq(null);
    } else {
      setExpandedFaq(index);
    }
  };

  // Update the height of the answer element when expanded
  useEffect(() => {
    answerRefs.current.forEach((ref, index) => {
      if (ref) {
        if (index === expandedFaq) {
          ref.style.height = `${ref.scrollHeight}px`;
        } else {
          ref.style.height = "0px";
        }
      }
    });
  }, [expandedFaq]);

  return (
    <>
      <HeroStyle2
        showFullImageOnMobile
        heroImageUrl="/miami-ortho-6.jpg"
        title={getLocalization("title")}
        backgroundPosition="top"
      />

      <Flex
        className={`section ${styles.mainContentSection}`}
        width="100%"
        align="center"
        justify="center"
      >
        <Flex
          direction="column"
          className={styles.contentSection}
          gap="8"
          p="5"
        >
          {/* Introduction Section */}
          <Flex direction="column" gap="6">
            <Heading as="h2" className={styles.heading}>
              {getLocalization("subtitle")}
            </Heading>
            <Text size="5" className={styles.paragraph}>
              {getLocalization("introFirstParagraph")}
            </Text>
            <Text size="5" className={styles.paragraph}>
              {getLocalization("introSecondParagraph")}
            </Text>
            <Text size="5">{getLocalization("introThirdParagraph")}</Text>
          </Flex>

          {/* More Adults Are Pursuing Orthodontics Section */}
          <Flex direction="column" gap="4">
            <Heading as="h3" className={styles.subsectionHeading}>
              {getLocalization("moreAdultsTitle")}
            </Heading>
            <Text size="5" className={styles.paragraph}>
              {getLocalization("moreAdultsIntro")}
            </Text>
            <Text size="5" className={styles.paragraph}>
              {getLocalization("moreAdultsWhy")}
            </Text>

            <Grid columns="12" gap="6">
              {/* Looking Good */}
              <Flex
                direction="column"
                gap="2"
                gridColumn={{ initial: "1 / 13", sm: "1 / 5" }}
              >
                <Heading as="h4" className={styles.subheading}>
                  {getLocalization("lookingGoodTitle")}
                </Heading>
                <Text size="5">{getLocalization("lookingGoodContent")}</Text>
              </Flex>

              {/* Teeth Can Shift */}
              <Flex
                direction="column"
                gap="2"
                gridColumn={{ initial: "1 / 13", sm: "5 / 9" }}
              >
                <Heading as="h4" className={styles.subheading}>
                  {getLocalization("teethCanShiftTitle")}
                </Heading>
                <Text size="5">{getLocalization("teethCanShiftContent")}</Text>
              </Flex>

              {/* Maintaining Dental Health */}
              <Flex
                direction="column"
                gap="2"
                gridColumn={{ initial: "1 / 13", sm: "9 / 13" }}
              >
                <Heading as="h4" className={styles.subheading}>
                  {getLocalization("dentalHealthTitle")}
                </Heading>
                <Text size="5">{getLocalization("dentalHealthContent")}</Text>
              </Flex>
            </Grid>
          </Flex>
        </Flex>
      </Flex>

      {/* FAQ Section with blue background */}
      <Box className={styles.faqSection}>
        <Box className={styles.faqContainer}>
          <Heading as="h2" className={styles.faqTitle}>
            {getLocalization("faqTitle")}
          </Heading>

          {getLocalization("faqItems").map((faq, index) => (
            <div key={faq.question} className={styles.faqItem}>
              <button
                type="button"
                className={styles.faqQuestion}
                onClick={() => toggleFaq(index)}
                aria-expanded={expandedFaq === index}
              >
                {faq.question}
                <span
                  className={`${styles.faqPlusIcon} ${
                    expandedFaq === index ? styles.faqPlusIconOpen : ""
                  }`}
                >
                  +
                </span>
              </button>
              <div
                ref={(el) => {
                  answerRefs.current[index] = el;
                }}
                className={`${styles.faqAnswer} ${
                  expandedFaq === index ? styles.faqAnswerOpen : ""
                }`}
              >
                <Flex direction="column" gap="4">
                  {faq.answer.map((paragraph) => (
                    <Text key={paragraph} size="5">
                      {paragraph.includes("including Invisalign, here") ? (
                        <>
                          Dr. Garcia-Paul can advise you about options that will
                          fit your particular needs. You can find more
                          information about types of braces, including{" "}
                          <Link to="/our-treatments" className={styles.link}>
                            Invisalign, here
                          </Link>
                          .
                        </>
                      ) : paragraph.includes("incluido Invisalign, aquí") ? (
                        <>
                          La Dra. García-Paul puede aconsejarle sobre las
                          opciones que se adaptarán a sus necesidades
                          particulares. Puede encontrar más información sobre los
                          tipos de brackets, incluido{" "}
                          <Link to="/our-treatments" className={styles.link}>
                            Invisalign, aquí
                          </Link>
                          .
                        </>
                      ) : (
                        paragraph
                      )}
                    </Text>
                  ))}
                </Flex>
              </div>
            </div>
          ))}
        </Box>
      </Box>

      <Box className={styles.form}>
        <HomeConsultForm />
      </Box>
    </>
  );
}
